import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check, X, FileText, GitBranch } from 'lucide-react';

import { docsService } from '../../services/docs/docsService';
import type { DocsProcess, DocsProcedure } from '../../services/docs/types';

const DocsReviewQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const [processes, setProcesses] = useState<DocsProcess[]>([]);
  const [procedures, setProcedures] = useState<DocsProcedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const fetchQueue = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await docsService.getReviewQueue();
      setProcesses(data.processes);
      setProcedures(data.procedures);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load review queue'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, []);

  const handleApproveProcess = async (id: string) => {
    setActionInProgress(id);
    try {
      await docsService.approveProcessRevision(id);
      await fetchQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRejectProcess = async (id: string) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    setActionInProgress(id);
    try {
      await docsService.rejectProcessRevision(id, reason);
      await fetchQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleApproveProcedure = async (id: string) => {
    setActionInProgress(id);
    try {
      await docsService.approveProcedureRevision(id);
      await fetchQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRejectProcedure = async (id: string) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    setActionInProgress(id);
    try {
      await docsService.rejectProcedureRevision(id, reason);
      await fetchQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setActionInProgress(null);
    }
  };

  const totalItems = processes.length + procedures.length;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/docs')}
        className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Docs
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-2">Review Queue</h1>
      <p className="text-gray-500 mb-6">
        {totalItems} item{totalItems !== 1 ? 's' : ''} awaiting approval.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading && (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded-lg" />
          ))}
        </div>
      )}

      {!loading && totalItems === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400">
          No items pending approval.
        </div>
      )}

      {/* Processes */}
      {processes.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
            Processes ({processes.length})
          </h2>
          <div className="space-y-2">
            {processes.map((proc) => (
              <div
                key={proc.id}
                className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200"
              >
                <div className="flex items-center">
                  <GitBranch className="w-4 h-4 text-blue-500 mr-3" />
                  <div>
                    <Link
                      to={`/docs/processes/${proc.slug}`}
                      className="font-medium text-gray-900 hover:text-blue-600"
                    >
                      {proc.title}
                    </Link>
                    {proc.summary && (
                      <p className="text-sm text-gray-500 mt-0.5">
                        {proc.summary}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleApproveProcess(proc.id)}
                    disabled={actionInProgress === proc.id}
                    className="flex items-center px-3 py-1.5 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleRejectProcess(proc.id)}
                    disabled={actionInProgress === proc.id}
                    className="flex items-center px-3 py-1.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Procedures */}
      {procedures.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
            Procedures ({procedures.length})
          </h2>
          <div className="space-y-2">
            {procedures.map((proc) => (
              <div
                key={proc.id}
                className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200"
              >
                <div className="flex items-center">
                  <FileText className="w-4 h-4 text-purple-500 mr-3" />
                  <div>
                    <Link
                      to={`/docs/procedures/${proc.slug}`}
                      className="font-medium text-gray-900 hover:text-blue-600"
                    >
                      {proc.title}
                    </Link>
                    <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                      {proc.type.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleApproveProcedure(proc.id)}
                    disabled={actionInProgress === proc.id}
                    className="flex items-center px-3 py-1.5 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleRejectProcedure(proc.id)}
                    disabled={actionInProgress === proc.id}
                    className="flex items-center px-3 py-1.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocsReviewQueuePage;
